import React, { useEffect, useState } from 'react';
import { useRealKmClient } from '../../lib/useRealKmClient.js';

/* ═══════════════════════════════════════════════════════════
   Risposte Self-Assessment — richiesto esplicitamente in call il 23/09
   (Chiara/Feola: "clicco sul nome e vedo le risposte, mi serve un altro
   pulsante"). Usa GET /resources/{id}/questionari (Monaco, 23/09) —
   risposte grezze, domanda per domanda, non rielaborate. "compilazioni"
   è una lista: copre anche lo storico di compilazioni multiple nel
   tempo, non solo l'ultima — trovato verificando la forma reale della
   risposta, non presunto dalla richiesta originale.

   Le domande non risposte arrivano con risposta: null (confermato da
   Monaco) — mostrate comunque, per onestà su cosa manca, non nascoste.
═══════════════════════════════════════════════════════════ */

const T = {
  border: 'rgba(0,0,0,0.12)',
  surface: '#FFFFFF',
  surface2: '#F4F3EF',
  txt: '#1A1916',
  txt2: '#6B6A65',
  txt3: '#A3A19C',
  accent: '#185FA5',
  redBg: '#FCEBEB',
  redTxt: '#791F1F',
};

/* Una risposta può essere testo, lista, voto o oggetto (matrice di voti,
   recapiti). L'esito «non comprensibile» (UC-SA-02, FA2) arriva come
   {esito: 'non_comprensibile'}: con String() diventava «[object Object]». */
function isNonComprensibile(r) {
  return r && typeof r === 'object' && !Array.isArray(r) && r.esito === 'non_comprensibile';
}

function formatRisposta(r) {
  if (Array.isArray(r)) return r.map(formatRisposta).join(', ');
  if (r && typeof r === 'object') {
    return Object.entries(r).map(([k, v]) => `${k}: ${formatRisposta(v)}`).join(' · ');
  }
  return String(r);
}

function formatData(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('it-IT'); } catch { return iso; }
}

export default function RisposteSelfAssessmentModal({ resourceId, nome, tipo = 'resource', onClose }) {
  const kmClient = useRealKmClient();
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [compilazioni, setCompilazioni] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [selezionata, setSelezionata] = useState(0);

  useEffect(() => {
    if (!resourceId) return;
    setStatus('loading');
    const path = tipo === 'esperienza'
      ? `/esperienze/${encodeURIComponent(resourceId)}/questionari`
      : `/resources/${encodeURIComponent(resourceId)}/questionari`;
    kmClient
      .get(path)
      .then((data) => {
        const lista = Array.isArray(data?.compilazioni) ? data.compilazioni : [];
        // Più recenti prima — completata_il se c'è, altrimenti aperta_il.
        lista.sort((a, b) => new Date(b.completata_il || b.aperta_il) - new Date(a.completata_il || a.aperta_il));
        setCompilazioni(lista);
        setSelezionata(0);
        setStatus('ready');
      })
      .catch((err) => {
        setErrorMessage(err?.detail || 'Errore nel caricamento delle risposte.');
        setStatus('error');
      });
  }, [kmClient, resourceId, tipo]);

  const corrente = compilazioni[selezionata];

  // Raggruppa le domande per sezione, rispettando sezione_ordine — non
  // l'ordine di arrivo dall'API, che potrebbe non essere garantito.
  const sezioni = React.useMemo(() => {
    if (!corrente?.domande) return [];
    const mappa = new Map();
    for (const d of corrente.domande) {
      const key = d.sezione_codice || '_';
      if (!mappa.has(key)) mappa.set(key, { nome: d.sezione || 'Sezione', ordine: d.sezione_ordine ?? 999, domande: [] });
      mappa.get(key).domande.push(d);
    }
    return [...mappa.values()].sort((a, b) => a.ordine - b.ordine);
  }, [corrente]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: T.surface, borderRadius: 14, width: '100%', maxWidth: 720, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.25)' }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.txt }}>Risposte Self-Assessment</div>
            <div style={{ fontSize: 12.5, color: T.txt2 }}>{nome || resourceId}</div>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: T.txt2, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          {status === 'loading' && <div style={{ color: T.txt3, textAlign: 'center', padding: 20 }}>Caricamento…</div>}
          {status === 'error' && <div style={{ padding: 12, borderRadius: 8, background: T.redBg, color: T.redTxt, fontSize: 13 }}>{errorMessage}</div>}

          {status === 'ready' && compilazioni.length === 0 && (
            <div style={{ color: T.txt3, textAlign: 'center', padding: 20 }}>
              Nessun Self-Assessment compilato finora.
            </div>
          )}

          {status === 'ready' && compilazioni.length > 0 && (
            <>
              {compilazioni.length > 1 && (
                <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {compilazioni.map((c, i) => (
                    <button
                      key={c.istanza_id}
                      type="button"
                      onClick={() => setSelezionata(i)}
                      style={{
                        padding: '5px 11px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                        border: `1px solid ${i === selezionata ? T.accent : T.border}`,
                        background: i === selezionata ? T.accent : T.surface,
                        color: i === selezionata ? '#fff' : T.txt2,
                      }}
                    >
                      {c.questionario_nome?.split('—')[0]?.trim() || c.questionario} · {formatData(c.completata_il || c.aperta_il).split(',')[0]}
                    </button>
                  ))}
                </div>
              )}

              <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, background: T.surface2, fontSize: 12.5, color: T.txt2 }}>
                <strong style={{ color: T.txt }}>{corrente.questionario_nome || corrente.questionario}</strong>
                {' · '}Stato: {corrente.stato}
                {' · '}Aperta il {formatData(corrente.aperta_il)}
                {corrente.completata_il && <> · Completata il {formatData(corrente.completata_il)}</>}
              </div>

              {sezioni.map((sez) => (
                <div key={sez.nome} style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: T.accent, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 10 }}>
                    {sez.nome}
                  </div>
                  {sez.domande.map((d) => (
                    <div key={d.codice} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${T.border}` }}>
                      <div style={{ fontSize: 13, color: T.txt, marginBottom: 4 }}>{d.domanda}</div>
                      {d.risposta === null || d.risposta === undefined ? (
                        <div style={{ fontSize: 13, color: T.txt3, fontStyle: 'italic' }}>Non risposto</div>
                      ) : isNonComprensibile(d.risposta) ? (
                        <div style={{ fontSize: 13, color: T.redTxt, fontStyle: 'italic' }}>Risposta non comprensibile (dopo due tentativi)</div>
                      ) : (
                        <div style={{ fontSize: 13.5, color: T.txt, fontWeight: 500 }}>
                          {formatRisposta(d.risposta)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
